import React from 'react';
import { Button } from '@/components/ui/button';
import { ArrowRight, ExternalLink } from 'lucide-react';
import { CTASectionContent, PageSectionProps, CTAButton } from '@/types/cms';
import { cn } from '@/lib/utils';

interface CTASectionProps extends PageSectionProps {
  content: CTASectionContent;
}

const CTASection: React.FC<CTASectionProps> = ({ 
  section, 
  content, 
  className,
  isPreview = false
}) => {
  const {
    subtitle,
    buttons = [],
    background_type = 'primary_card',
    background_image,
    background_color
  } = content as CTASectionContent;

  // Helper function to get responsive class prefix
  const getResponsivePrefix = () => isPreview ? '@' : '';

  const backgroundStyles = React.useMemo(() => {
    switch (background_type) {
      case 'image':
        return background_image
          ? { 
              backgroundImage: `url(${background_image})`, 
              backgroundSize: 'cover', 
              backgroundPosition: 'center' 
            }
          : {};
      case 'color':
        return { backgroundColor: background_color || 'hsl(var(--secondary))' };
      case 'gradient':
        return {
          background: 'linear-gradient(135deg, hsl(var(--primary)) 0%, hsl(var(--primary) / 0.8) 100%)',
        };
      case 'primary_card':
        return {
          backgroundColor: 'hsl(var(--background))',
        };
      case 'minimal':
        return { backgroundColor: 'hsl(var(--secondary))' };
      default:
        return { backgroundColor: 'hsl(var(--secondary))' };
    }
  }, [background_type, background_image, background_color]);

  const isImageOrGradientBg = background_type === 'image' || background_type === 'gradient';
  const isPrimaryCard = background_type === 'primary_card';

  const ButtonComponent: React.FC<{ button: CTAButton; index: number }> = ({ button, index }) => {
    const { text, link, variant = 'primary' } = button;
    const isExternal = link.startsWith('http') || link.startsWith('//');

    const buttonVariant = (isImageOrGradientBg || isPrimaryCard)
      ? (variant === 'primary' ? 'secondary' : 'outline')
      : variant;

    const buttonClasses = cn(
      "min-w-[180px] h-12 text-lg font-semibold transition-all duration-300",
      (isImageOrGradientBg || isPrimaryCard) && variant === 'outline' && "border-white text-white hover:bg-white hover:text-gray-900",
      isPrimaryCard && variant === 'primary' && "bg-white text-primary hover:bg-white/90 transition-colors px-8 py-6 text-lg rounded-full shadow-lg hover:shadow-xl"
    );

    return (
      <Button 
        key={index}
        asChild
        variant={buttonVariant}
        size="lg"
        className={buttonClasses}
      >
        <a 
          href={link}
          target={isExternal ? '_blank' : '_self'}
          rel={isExternal ? 'noopener noreferrer' : undefined}
          className="inline-flex items-center"
        >
          {text}
          {isExternal ? (
            <ExternalLink className="ml-2 h-5 w-5" />
          ) : (
            <ArrowRight className="ml-2 h-5 w-5" />
          )}
        </a>
      </Button>
    );
  };

  if (isPrimaryCard) {
    // Primary card layout (matching Index.tsx CTA)
    return (
      <section 
        className={cn(`py-16 ${getResponsivePrefix()}lg:py-20 px-4 ${getResponsivePrefix()}md:px-6`, className)}
        style={backgroundStyles}
      >
        <div className="max-w-6xl mx-auto">
          <div className="rounded-3xl overflow-hidden shadow-xl relative">
            {/* Inner card background with primary gradient */}
            <div 
              className="absolute inset-0"
              style={{
                background: 'linear-gradient(135deg, hsl(var(--primary)) 0%, hsl(var(--primary) / 0.8) 100%)'
              }}
            />
            
            {/* Decorative elements (matching Index.tsx) */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -translate-y-1/3 translate-x-1/3" />
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/10 rounded-full translate-y-1/3 -translate-x-1/3" />
            
            <div className={`relative z-10 p-8 ${getResponsivePrefix()}md:p-12 ${getResponsivePrefix()}lg:p-16 text-white`}>
              <div className="text-center max-w-3xl mx-auto">
                {/* Main Title */}
                {section.title && (
                  <h2 className={`text-3xl ${getResponsivePrefix()}md:text-4xl font-bold mb-6 text-white`}>
                    {section.title}
                  </h2>
                )}

                {/* Subtitle */}
                {subtitle && (
                  <p className="mb-8 text-white/90 text-lg">
                    {subtitle}
                  </p>
                )}

                {/* Action Buttons */}
                {buttons.length > 0 ? (
                  <div className={`flex flex-col ${getResponsivePrefix()}sm:flex-row gap-4 justify-center items-center flex-wrap`}>
                    {buttons.map((button, index) => (
                      <Button 
                        key={index}
                        asChild
                        className="bg-white text-primary hover:bg-white/90 transition-colors px-8 py-6 text-lg rounded-full shadow-lg hover:shadow-xl min-w-[180px]"
                      >
                        <a 
                          href={button.link}
                          target={button.link.startsWith('http') || button.link.startsWith('//') ? '_blank' : '_self'}
                          rel={button.link.startsWith('http') || button.link.startsWith('//') ? 'noopener noreferrer' : undefined}
                          className="inline-flex items-center"
                        >
                          {button.text}
                          {button.link.startsWith('http') || button.link.startsWith('//') ? (
                            <ExternalLink className="ml-2 h-5 w-5" />
                          ) : (
                            <ArrowRight className="ml-2 h-5 w-5" />
                          )}
                        </a>
                      </Button>
                    ))}
                  </div>
                ) : (
                  // Empty state for admin preview
                  <div className="py-8 text-center text-white/70">
                    <p className="text-lg">No action buttons configured yet.</p>
                    <p className="text-sm mt-2">Add buttons through the admin panel to see them here.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  // Regular layout for other background types
  return (
    <section 
      className={cn("relative py-20 px-4 overflow-hidden", className)}
      style={backgroundStyles}
    >
      {/* Overlay for better text readability on image backgrounds */}
      {background_type === 'image' && (
        <div className="absolute inset-0 bg-black/50" />
      )}

      {/* Content */}
      <div className="relative z-10 max-w-4xl mx-auto text-center">
        {/* Main Title */}
        {section.title && (
          <h2 className={cn(
            `text-3xl ${getResponsivePrefix()}md:text-5xl font-bold mb-6 leading-tight`,
            isImageOrGradientBg ? "text-white" : "text-foreground"
          )}>
            {section.title}
          </h2>
        )}

        {/* Subtitle */}
        {subtitle && (
          <p className={cn(
            `text-xl ${getResponsivePrefix()}md:text-2xl mb-12 leading-relaxed max-w-2xl mx-auto`,
            isImageOrGradientBg ? "text-white/90" : "text-muted-foreground"
          )}>
            {subtitle}
          </p>
        )}

        {/* Action Buttons */}
        {buttons.length > 0 ? (
          <div className={`flex flex-col ${getResponsivePrefix()}sm:flex-row gap-4 justify-center items-center flex-wrap`}>
            {buttons.map((button, index) => (
              <ButtonComponent key={index} button={button} index={index} />
            ))}
          </div>
        ) : (
          // Empty state for admin preview
          <div className={cn(
            "py-8 text-center",
            isImageOrGradientBg ? "text-white/70" : "text-muted-foreground"
          )}>
            <p className="text-lg">No action buttons configured yet.</p>
            <p className="text-sm mt-2">Add buttons through the admin panel to see them here.</p>
          </div>
        )}
      </div>

      {/* Decorative elements (matching Index.tsx CTA) */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -translate-y-1/3 translate-x-1/3" />
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/10 rounded-full translate-y-1/3 -translate-x-1/3" />
      </div>
    </section>
  );
};

export default CTASection;